import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { ZoomIn, Check, RefreshCw, X } from "lucide-react";
import { getCroppedProfileBlob } from "@/lib/imageUpload";

export default function ImageCropModal({ imageSrc, onConfirm, onChooseAgain, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_area, areaPx) => setAreaPixels(areaPx), []);

  const confirm = async () => {
    if (!areaPixels) return;
    setProcessing(true);
    try {
      const file = await getCroppedProfileBlob(imageSrc, areaPixels);
      onConfirm(file);
    } catch (e) {
      onConfirm(null, e);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      data-testid="image-crop-modal"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
    >
      <div className="w-full max-w-md bg-[#F9F8F6] rounded-3xl overflow-hidden shadow-2xl border border-stone-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <div className="overline">Adjust your photo</div>
            <div className="text-xs text-stone-500 mt-0.5">Pinch to zoom · drag to reposition</div>
          </div>
          <button
            data-testid="crop-close-btn"
            onClick={onCancel}
            className="w-8 h-8 rounded-full grid place-items-center hover:bg-stone-200 text-stone-500"
          >
            <X size={16} />
          </button>
        </div>

        <div className="relative h-72 bg-stone-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center gap-3">
            <ZoomIn size={16} className="text-stone-500 shrink-0" />
            <input
              data-testid="crop-zoom-slider"
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-[#1E1B4B]"
            />
          </div>

          <div className="mt-5 flex gap-3">
            <button
              data-testid="crop-choose-again-btn"
              onClick={onChooseAgain}
              disabled={processing}
              className="flex-1 btn-pill inline-flex items-center justify-center gap-2 text-sm border border-stone-300 bg-white text-stone-700 px-5 py-2.5 disabled:opacity-40"
            >
              <RefreshCw size={15} /> Choose Again
            </button>
            <button
              data-testid="crop-confirm-btn"
              onClick={confirm}
              disabled={processing || !areaPixels}
              className="flex-1 btn-pill inline-flex items-center justify-center gap-2 text-sm text-white px-5 py-2.5 disabled:opacity-40"
              style={{ background: "#1E1B4B" }}
            >
              <Check size={15} /> {processing ? "Processing…" : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
